/**
 * utils/connectionPool/manager.ts — ConnectionPoolManager core class
 */

import type { PoolConfig, PooledConnection } from './types.js';
import { applyAcquireMethods } from './acquire.js';
import { applyMaintenanceMethods } from './maintenance.js';
import { applyStatsMethods } from './stats.js';

/**
 * Connection Pool Manager
 * Manages Supabase connections efficiently
 */
export class ConnectionPoolManager {
    private static instance: ConnectionPoolManager;
    private connections: Map<string, PooledConnection> = new Map();
    private config: Required<PoolConfig>;
    private acquireQueue: Array<{
        resolve: (conn: string) => void;
        reject: (err: Error) => void;
    }> = [];
    private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

    private constructor(config: PoolConfig = {}) {
        this.config = {
            maxConnections: config.maxConnections ?? 10,
            minConnections: config.minConnections ?? 2,
            idleTimeout: config.idleTimeout ?? 30 * 60 * 1000, // 30 minutes
            acquireTimeout: config.acquireTimeout ?? 10 * 1000, // 10 seconds
            connectionString: config.connectionString ?? '',
        };

        // Initialize minimum connections
        this.initializePool();
    }

    /**
     * Get singleton instance
     */
    static getInstance(config?: PoolConfig): ConnectionPoolManager {
        if (!ConnectionPoolManager.instance) {
            ConnectionPoolManager.instance = new ConnectionPoolManager(config);
        }
        return ConnectionPoolManager.instance;
    }

    /**
     * Initialize minimum connections
     */
    private initializePool(): void {
        for (let i = 0; i < this.config.minConnections; i++) {
            this.createConnection();
        }

        // Start cleanup interval - only if not already running
        if (this.cleanupIntervalId === null) {
            this.cleanupIntervalId = setInterval(() => this.cleanupIdleConnections(), 60 * 1000); // Every minute

            // CRITICAL: Cleanup on page unload to prevent lingering intervals
            if (typeof window !== 'undefined') {
                window.addEventListener('beforeunload', () => {
                    this.destroy();
                });
            }
        }
    }

    /**
     * Create new connection
     */
    private createConnection(): string {
        if (this.connections.size >= this.config.maxConnections) {
            throw new Error('Connection pool exhausted');
        }

        const id = `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.connections.set(id, {
            id,
            createdAt: Date.now(),
            lastUsed: Date.now(),
            inUse: false,
        });

        if (import.meta.env.DEV) {
            console.log(`[Pool] Connection created: ${id} (${this.connections.size}/${this.config.maxConnections})`);
        }

        return id;
    }
}

applyAcquireMethods();
applyMaintenanceMethods();
applyStatsMethods();
